'use server'

import { apiRequest, ApiResponse } from '../request'

export type WhatsappTemplateCategory =
    | 'MARKETING'
    | 'UTILITY'
    | 'AUTHENTICATION'

export type WhatsappTemplateContentType =
    | 'TEXT'
    | 'MEDIA'
    | 'QUICK_REPLY'
    | 'CALL_TO_ACTION'
    | 'CARD'

export type WhatsappTemplateApprovalStatus =
    | 'DRAFT'
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'PAUSED'
    | 'DISABLED'

export interface WhatsappTemplateVariable {
    index: number
    name?: string
    token?: string
    example?: string
}

export interface TemplateToken {
    key: string
    label: string
    entity: 'client' | 'user' | 'company' | 'context'
    example: string
}

export interface WhatsappTemplateButton {
    type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
    text: string
    url?: string
    phone?: string
}

export interface WhatsappTemplate {
    id: number
    companyId: number
    name: string
    friendlyName: string
    category: WhatsappTemplateCategory
    contentType: WhatsappTemplateContentType
    language: string
    /** Legado: identificador do provedor anterior. Não identifica template novo. */
    contentSid: string | null
    /** Id do template na Meta; só existe depois de submetido. */
    metaTemplateId: string | null
    bodyText: string
    bodyTemplate: string | null
    headerText: string | null
    footerText: string | null
    mediaUrl: string | null
    buttons: WhatsappTemplateButton[] | null
    variables: WhatsappTemplateVariable[] | null
    usageScope: string | null
    approvalStatus: WhatsappTemplateApprovalStatus
    approvalRejectionReason: string | null
    lastSyncedAt: string | null
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface CreateWhatsappTemplateData {
    name: string
    friendlyName: string
    category: WhatsappTemplateCategory
    contentType?: WhatsappTemplateContentType
    language?: string
    bodyText: string
    headerText?: string
    footerText?: string
    mediaUrl?: string
    buttons?: WhatsappTemplateButton[]
    variables?: WhatsappTemplateVariable[]
    active?: boolean
    contentSid?: string
}

export type UpdateWhatsappTemplateData = Partial<
    Omit<CreateWhatsappTemplateData, 'name'>
>

export interface WhatsappTemplateFilters {
    active?: boolean
    category?: WhatsappTemplateCategory
    approvalStatus?: WhatsappTemplateApprovalStatus
    usageScope?: string
}

export const WHATSAPP_TEMPLATE_CATEGORY_LABELS: Record<
    WhatsappTemplateCategory,
    string
> = {
    MARKETING: 'Marketing',
    UTILITY: 'Utilidade',
    AUTHENTICATION: 'Autenticação',
}

export const WHATSAPP_TEMPLATE_CONTENT_TYPE_LABELS: Record<
    WhatsappTemplateContentType,
    string
> = {
    TEXT: 'Texto',
    MEDIA: 'Mídia',
    QUICK_REPLY: 'Resposta Rápida',
    CALL_TO_ACTION: 'Call to Action',
    CARD: 'Card',
}

export const WHATSAPP_TEMPLATE_APPROVAL_STATUS_LABELS: Record<
    WhatsappTemplateApprovalStatus,
    string
> = {
    DRAFT: 'Rascunho',
    PENDING: 'Em Aprovação',
    APPROVED: 'Aprovado',
    REJECTED: 'Rejeitado',
    PAUSED: 'Pausado',
    DISABLED: 'Desabilitado',
}

export const WHATSAPP_TEMPLATE_APPROVAL_STATUS_COLORS: Record<
    WhatsappTemplateApprovalStatus,
    string
> = {
    DRAFT: 'badge-ghost',
    PENDING: 'badge-warning',
    APPROVED: 'badge-success',
    REJECTED: 'badge-error',
    PAUSED: 'badge-warning',
    DISABLED: 'badge-neutral',
}

export async function getWhatsappTemplates(
    filters?: WhatsappTemplateFilters,
): Promise<ApiResponse<WhatsappTemplate[]>> {
    return apiRequest<WhatsappTemplate[]>(
        'messaging/whatsapp-templates',
        { params: filters as any },
    )
}

/**
 * Templates que o operador pode enviar manualmente.
 *
 * Passe o setor do atendimento: em setor WhatsApp Web a aprovação da Meta não é
 * exigida (lá o corpo vai como texto comum). Sem o setor, a resposta é
 * conservadora — só aprovados.
 */
export async function getSendableWhatsappTemplates(
    departmentId?: number | null,
): Promise<ApiResponse<WhatsappTemplate[]>> {
    return apiRequest<WhatsappTemplate[]>(
        'messaging/whatsapp-templates/sendable',
        { params: departmentId != null ? { departmentId } : undefined },
    )
}

/**
 * Escopos de notificação SEM template aprovado pela Meta.
 *
 * No WhatsApp Web eles funcionam com o corpo cadastrado (ou o texto padrão); ao
 * mudar um setor para a API oficial, estes deixam de ser enviados.
 */
export async function getWhatsappChannelReadiness(): Promise<
    ApiResponse<{ missingScopes: string[] }>
> {
    return apiRequest<{ missingScopes: string[] }>(
        'messaging/whatsapp-templates/channel-readiness',
        {}
    )
}

export async function getWhatsappTemplateTokens(): Promise<
    ApiResponse<TemplateToken[]>
> {
    return apiRequest<TemplateToken[]>(
        'messaging/whatsapp-templates/tokens',
        {},
    )
}

export async function getWhatsappTemplate(
    id: number,
): Promise<ApiResponse<WhatsappTemplate>> {
    return apiRequest<WhatsappTemplate>(
        `messaging/whatsapp-templates/${id}`,
        {},
    )
}

export async function createWhatsappTemplate(
    data: CreateWhatsappTemplateData,
): Promise<ApiResponse<WhatsappTemplate>> {
    return apiRequest<WhatsappTemplate>(
        'messaging/whatsapp-templates',
        { data },
        'post',
    )
}

export async function updateWhatsappTemplate(
    id: number,
    data: UpdateWhatsappTemplateData,
): Promise<ApiResponse<WhatsappTemplate>> {
    return apiRequest<WhatsappTemplate>(
        `messaging/whatsapp-templates/${id}`,
        { data },
        'patch',
    )
}

export async function deleteWhatsappTemplate(
    id: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        `messaging/whatsapp-templates/${id}`,
        {},
        'delete',
    )
}

export async function submitWhatsappTemplateApproval(
    id: number,
): Promise<ApiResponse<WhatsappTemplate>> {
    return apiRequest<WhatsappTemplate>(
        `messaging/whatsapp-templates/${id}/submit-approval`,
        {},
        'post',
    )
}

export async function syncWhatsappTemplate(
    id: number,
): Promise<ApiResponse<WhatsappTemplate>> {
    return apiRequest<WhatsappTemplate>(
        `messaging/whatsapp-templates/${id}/sync`,
        {},
        'post',
    )
}

export async function syncAllWhatsappTemplates(): Promise<
    ApiResponse<{ synced: number }>
> {
    return apiRequest<{ synced: number }>(
        'messaging/whatsapp-templates/sync-all',
        {},
        'post',
    )
}

export interface WhatsappTemplatePreviewResult {
    rendered: string
    header?: string
    footer?: string
    buttons?: WhatsappTemplateButton[] | null
    mediaUrl?: string | null
}

export async function previewWhatsappTemplate(
    id: number,
    values?: Record<string, string>,
): Promise<ApiResponse<WhatsappTemplatePreviewResult>> {
    return apiRequest<WhatsappTemplatePreviewResult>(
        `messaging/whatsapp-templates/${id}/preview`,
        { data: { values } },
        'post',
    )
}

/** Relatório da adoção dos templates que já existem na conta da Meta. */
export interface AdoptFromMetaResult {
    adopted: number
    alreadyAdopted: number
    unmatchedLocal: { id: number; name: string; language: string }[]
    unmatchedRemote: { name: string; language: string; status: string }[]
}

/**
 * Reconhece os templates que já existem na WABA. Não cria nem reaprova nenhum.
 */
export async function adoptTemplatesFromMeta(): Promise<ApiResponse<AdoptFromMetaResult>> {
    return apiRequest<AdoptFromMetaResult>('messaging/whatsapp-templates/adopt-from-meta', {}, 'post')
}
